import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useDeleteAccount, useMe } from "@/api/hooks";
import { TypedRemovalDialog } from "@/components/TypedRemovalDialog";
import { extractApiDetail } from "@/lib/apiDetail";
import { soleAdminOrgs, useAccountRemovalLists } from "@/lib/removal";

/** "Delete my account" (Privacy Policy 10.1-10.3): the warning, the typed
 *  email, and the sole-admin refusal explained. Used on the Account page
 *  and on the terms screen, where a person who will not accept can leave
 *  instead. A successful removal signs out (``useDeleteAccount``). */
export function SelfRemovalDialog(props: {
  open: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const del = useDeleteAccount();
  const lists = useAccountRemovalLists();
  const [error, setError] = useState<string | null>(null);
  const email = me.data?.user.email ?? "";

  const handleDelete = async () => {
    setError(null);
    try {
      await del.mutateAsync();
    } catch (e) {
      const orgs = soleAdminOrgs(e);
      setError(
        orgs
          ? t("removal.self.sole_admin", { orgs: orgs.join(", ") })
          : extractApiDetail(e),
      );
    }
  };

  return (
    <TypedRemovalDialog
      open={props.open}
      onClose={() => {
        setError(null);
        props.onClose();
      }}
      title={t("removal.self.title")}
      intro={t("removal.self.intro")}
      deleted={lists.deleted}
      kept={lists.kept}
      confirmLabel={t("removal.user.confirm_label", { email })}
      expected={email}
      actionLabel={t("account.delete_account")}
      busy={del.isPending}
      error={error}
      onConfirm={() => void handleDelete()}
    />
  );
}
